import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  type AdminCertificationVerdict,
  addAdminCertificationComment,
  approveAdminCertification,
  fetchAdminCertification,
  fetchAdminCertificationComments,
  markAdminCertificationNotVerifiable,
  rejectAdminCertification,
  updateAdminCertification,
} from '@/api/(admin)/certifications';
import type { UpdateCertificationPayload } from '@/api/certifications/types';
import { adminCertificationLinkedStylesKey } from '@/hooks/useCertificationsQuery';
import { useTranslator } from '@/hooks/useTranslator';
import { openFileInNewTab } from '@/lib/utils/openFileInNewTab';
import { buildAdminClaimUpdatePayload } from '../-utils/buildAdminClaimUpdatePayload';
import { adminCertificationsQueryKey } from './useAdminCertifications';

export function adminCertificationQueryOptions(id: string) {
  return queryOptions({
    queryKey: ['admin', 'certifications', 'detail', id] as const,
    queryFn: () => fetchAdminCertification(id).then(({ data }) => data),
    enabled: Boolean(id),
  });
}

/**
 * One certificate, as an operator sees it: the record, its comments, the three
 * review verdicts, and the inline edits.
 *
 * The old dashboard split this across two screens — a read-only `/view` and a
 * `/review` that carried the form. v2 edits in place, so there is one page and
 * the verdicts sit in its header.
 */
export function useAdminCertificationDetail(id: string) {
  const { getTranslation: t } = useTranslator({
    page: 'managementCertifications',
  });
  const queryClient = useQueryClient();

  const query = useQuery(adminCertificationQueryOptions(id));
  const cert = query.data;

  const comments = useQuery({
    queryKey: ['admin', 'certifications', 'comments', id] as const,
    queryFn: () => fetchAdminCertificationComments(id).then(({ data }) => data),
    enabled: Boolean(id),
  });

  const updateMutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      updateAdminCertification(id, payload),
  });
  const verdictMutation = useMutation({
    mutationFn: async (verdict: AdminCertificationVerdict) => {
      if (verdict === 'approve') await approveAdminCertification(id);
      else if (verdict === 'reject') await rejectAdminCertification(id);
      else await markAdminCertificationNotVerifiable(id);
    },
  });
  const commentMutation = useMutation({
    mutationFn: (content: string) =>
      addAdminCertificationComment({
        claimId: id,
        companyId: cert?.companyId ?? '',
        content,
      }),
  });

  /** The detail row and every list that might show it. */
  function invalidate(): void {
    void queryClient.invalidateQueries({
      queryKey: ['admin', 'certifications', 'detail', id],
    });
    void queryClient.invalidateQueries({
      queryKey: adminCertificationsQueryKey,
    });
    void queryClient.invalidateQueries({
      queryKey: adminCertificationLinkedStylesKey(id),
    });
  }

  async function update(patch: UpdateCertificationPayload): Promise<void> {
    if (!cert) return;
    try {
      // Merged, not patched: `PUT /:id` replaces the record, so anything left
      // out is dropped.
      await updateMutation.mutateAsync(
        buildAdminClaimUpdatePayload(cert, patch),
      );
      toast.success(t('detail.saved'));
      invalidate();
    } catch (error) {
      toast.error(t('detail.saveFailed'), {
        description: error instanceof Error ? error.message : undefined,
      });
    }
  }

  async function setVerdict(verdict: AdminCertificationVerdict): Promise<void> {
    try {
      await verdictMutation.mutateAsync(verdict);
      toast.success(t(`detail.verdict.${verdict}Success`));
      invalidate();
    } catch (error) {
      toast.error(t('detail.verdict.failed'), {
        description: error instanceof Error ? error.message : undefined,
      });
    }
  }

  async function addComment(content: string): Promise<boolean> {
    const trimmed = content.trim();
    if (trimmed === '') return false;
    try {
      await commentMutation.mutateAsync(trimmed);
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'certifications', 'comments', id],
      });
      return true;
    } catch (error) {
      toast.error(t('detail.commentFailed'), {
        description: error instanceof Error ? error.message : undefined,
      });
      return false;
    }
  }

  return {
    cert,
    isLoading: query.isLoading,
    isError: query.isError,
    comments: comments.data ?? [],
    isCommentsLoading: comments.isLoading,
    update,
    isSaving: updateMutation.isPending,
    setVerdict,
    pendingVerdict: verdictMutation.isPending
      ? verdictMutation.variables
      : null,
    addComment,
    isAddingComment: commentMutation.isPending,
    viewFile: () => openFileInNewTab(cert?.fileUrl),
  };
}
